<template>
  <main class="screen">
    <FatalError
      v-if="fatal"
      :message="fatal"
      @retry="start"
    />

    <template v-else-if="!session">
      <Intro
        :pos-machine-code="posMachineCode"
        :starting="starting"
        @start="start"
      />
    </template>

    <template v-else>
      <!-- WAITING_NOTIFY:販賣機尚未回報可交易 → 連線中(ack 後 server 轉 MACHINE_READY) -->
      <WaitingNotify
        v-if="session.state === S.WAITING_NOTIFY"
        title="連線販賣機中"
        subtitle="正在與販賣機建立連線,請稍候"
        hint-text="等待販賣機回應中..."
        :pos-machine-code="posMachineCode"
        :cancelling="cancelling"
        :started-at="sessionStartedAt"
        @cancel="onCancel"
      />

      <!-- MACHINE_READY / CASH_ONLY:直接請投幣(不選付款方式) -->
      <WaitingNotify
        v-else-if="session.state === S.MACHINE_READY && session.flow === NoScreenFlow.CASH_ONLY"
        title="請於販賣機投入現金購買"
        subtitle="投幣並選購後,系統會自動進入下一步"
        hint-text="等待販賣機回報中..."
        :timeout-seconds="60"
        :pos-machine-code="posMachineCode"
        :cancelling="cancelling"
        :started-at="machineAckedMs"
        @cancel="onCancel"
      />

      <!-- MACHINE_READY / MACHINE_DECIDE:機台決定金額/建單 → 請於販賣機操作 -->
      <WaitingNotify
        v-else-if="session.state === S.MACHINE_READY && session.flow === NoScreenFlow.MACHINE_DECIDE"
        title="請於販賣機操作"
        subtitle="請在販賣機上選擇商品,系統會自動進入下一步"
        hint-text="等待販賣機建立訂單中..."
        :timeout-seconds="60"
        :pos-machine-code="posMachineCode"
        :cancelling="cancelling"
        :started-at="machineAckedMs"
        @cancel="onCancel"
      />

      <!-- MACHINE_READY / PHONE_DECIDE:尚未選付款方式 → 電子 / 現金 二選一 -->
      <PayMethodChoice
        v-else-if="session.state === S.MACHINE_READY && !session.payMethod"
        :started-at="machineAckedMs"
        :selecting="selectingMethod"
        :cancelling="cancelling"
        :error="payError"
        @select="onSelectPayMethod"
        @cancel="onCancel"
      />

      <!-- MACHINE_READY / PHONE_DECIDE 已選現金 → 請投幣(等販賣機上報) -->
      <WaitingNotify
        v-else-if="session.state === S.MACHINE_READY && session.payMethod === 'cash'"
        title="請於販賣機投入現金購買"
        subtitle="投幣並選購後,系統會自動進入下一步"
        hint-text="等待販賣機回報中..."
        :timeout-seconds="60"
        :pos-machine-code="posMachineCode"
        :cancelling="cancelling"
        :started-at="machineAckedMs"
        @cancel="onCancel"
      />

      <!-- MACHINE_READY(選電子)或 WAITING_PAY / PAYING → 金額輸入 + 通路 + 付款/跳轉 -->
      <WaitingPay
        v-else-if="isMpayStep"
        :session="session"
        :common-amounts="commonAmounts"
        :mpay-list="mpayList"
        :require-amount="session.state === S.MACHINE_READY && session.payMethod === 'mpay'"
        :paying="paying"
        :cancelling="cancelling"
        :awaiting-external="awaitingExternalPay || session.state === S.PAYING"
        :wait-pay-started-at="machineAckedMs"
        :error="payError"
        @pay="onPay"
        @cancel="onCancel"
      />

      <DoneView
        v-else-if="session.state === S.PAID && session.supportInvoice === false"
        no-invoice
        @close="onClose"
      />

      <InvoiceForm
        v-else-if="session.state === S.PAID"
        :session="session"
        :submitting="submittingInvoice"
        :error="invoiceError"
        :paid-at="paidAt"
        @submit="onSubmitInvoice"
        @close="onClose"
      />

      <DoneView
        v-else-if="session.state === S.DONE"
        :donated="session.carrierType === CARRIER_DONATE"
        @close="onClose"
      />

      <StatusBanner
        v-else
        :state="session.state"
        @close="onClose"
      />
    </template>
  </main>
</template>



<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import WaitingNotify from '@/components/WaitingNotify.vue'
import WaitingPay from '@/components/WaitingPay.vue'
import PayMethodChoice from '@/components/PayMethodChoice.vue'
import InvoiceForm from '@/components/InvoiceForm.vue'
import DoneView from '@/components/DoneView.vue'
import StatusBanner from '@/components/StatusBanner.vue'
import FatalError from '@/components/FatalError.vue'
import Intro from '@/components/Intro.vue'
import {
  CARRIER_DONATE,
  NoScreenFlow,
  QrSessionState as S,
  TERMINAL_STATES,
  pickRedirectUrl,
  type PayChannel,
  type QrInvoiceBo,
  type QrPayMethod,
  type QrSessionVo,
} from '@/types/qr'
import { ApiError } from '@/api/http'
import {
  cancelSession,
  createSession,
  issueInvoice,
  payByMobile,
  pollSession,
  selectPayMethod,
} from '@/api/qrSession'
import {
  clearSessionId,
  loadCommonAmounts,
  loadMpayList,
  loadPaidAt,
  loadSessionId,
  loadSessionStartedAt,
  saveCommonAmounts,
  saveMpayList,
  savePaidAt,
  saveSessionId,
  saveSessionStartedAt,
} from '@/utils/storage'
import { friendlyError } from '@/utils/errorMessage'

const route = useRoute()
const router = useRouter()

const posMachineCode = computed(() => String(route.params.posMachineCode || ''))

const session = ref<QrSessionVo | null>(null)
/** 所有 WAITING_NOTIFY 倒數的共同起點:session 建立時間(≈ server createdAt)。
 *  server 逾時窗口是「建 session 起 60 秒」單一窗口,不再分段 reset。 */
const sessionStartedAt = ref<number>(Date.now())
const paidAt = ref<number>(0)
const selectingMethod = ref(false)
/** 常用金額:只有 createSession 回傳,存 ref + sessionStorage 以撐過 poll 覆蓋與重整 */
const commonAmounts = ref<number[] | null>(null)
/** 可用支付通路:同 commonAmounts,只有 createSession 回傳,需持久化 */
const mpayList = ref<string[] | null>(null)
const fatal = ref<string>('')
const starting = ref(false)
const cancelling = ref(false)
const paying = ref(false)
const awaitingExternalPay = ref(false)
const payError = ref('')
const submittingInvoice = ref(false)
const invoiceError = ref('')

/** 走「電子支付金額輸入 + 通路 + 跳轉付款」的 WaitingPay 畫面:
 *  MACHINE_READY 已選電子(手機輸入金額)或機台已建單(WAITING_PAY / PAYING)。 */
const isMpayStep = computed(() => {
  const s = session.value
  if (!s) return false
  return (
    (s.state === S.MACHINE_READY && s.payMethod === 'mpay') ||
    s.state === S.WAITING_PAY ||
    s.state === S.PAYING
  )
})

/** MACHINE_READY 各子態倒數起點:machine-ack 時間(server 逾時從此起算);parse 失敗用現在 */
const machineAckedMs = computed(() => {
  const at = session.value?.machineAckedAt
  if (!at) return Date.now()
  const t = new Date(at).getTime()
  return Number.isNaN(t) ? Date.now() : t
})

let pollTimer: number | null = null

function stopPolling() {
  if (pollTimer != null) {
    window.clearTimeout(pollTimer)
    pollTimer = null
  }
}

function pollInterval(_state: S | undefined): number {
  return 3000
}

async function pollOnce() {
  if (!session.value) return
  try {
    const next = await pollSession(posMachineCode.value, session.value.sessionId)
    session.value = next
  } catch (e) {
    /* session 找不到(000008)或參數錯誤(000004,如 qrCode/sessionId 失效)→ 停止輪詢顯示錯誤;
       其餘視為連線問題,讓下一輪重試 */
    if (e instanceof ApiError && (e.code === '000008' || e.code === '000004')) {
      fatal.value = friendlyError(e)
      stopPolling()
      return
    }
  }
}

function schedulePoll() {
  stopPolling()
  if (!session.value) return
  /* 終態就不再 poll,但 sessionStorage 不主動清掉;
     交給使用者按「關閉」→ onClose() 統一清 */
  if (TERMINAL_STATES.has(session.value.state)) return
  /* PAID 也繼續 poll,才能即時看到 server 端把 state 切走
     (例如 LINE Pay 端事後取消 → 11 CANCELED) */
  pollTimer = window.setTimeout(async () => {
    await pollOnce()
    schedulePoll()
  }, pollInterval(session.value.state))
}

watch(
  () => session.value?.state,
  (state, prev) => {
    schedulePoll()
    if (state !== prev && state !== S.WAITING_PAY && state !== S.PAYING) {
      /* 離開 WAITING_PAY / PAYING(進 PAID / CANCELED / EXPIRED / KICKED)
         才關掉「付款中」提示 */
      awaitingExternalPay.value = false
    }
    /* 進入 PAID(可開發票階段)時,起算 10 分鐘倒數 */
    if (state === S.PAID && prev !== S.PAID) {
      const existing = loadPaidAt(posMachineCode.value)
      if (existing) {
        paidAt.value = existing
      } else {
        const now = Date.now()
        paidAt.value = now
        savePaidAt(posMachineCode.value, now)
      }
    }
  },
)

/** 進頁時靜默 resume:只有 cache 內存在進行中 session 才接回,否則留在 Intro 等使用者按下「開始交易」。 */
async function resume() {
  if (!posMachineCode.value) {
    fatal.value = '缺少販賣機編號,請重新掃描 QR'
    return
  }
  const cached = loadSessionId(posMachineCode.value)
  if (!cached) return
  try {
    const next = await pollSession(posMachineCode.value, cached)
    /* 不論拿到什麼 state 都套上;
       終態(CANCELED / EXPIRED / KICKED / DONE)由模板走 StatusBanner / DoneView 顯示,
       sessionStorage 由 schedulePoll 遇終態時清理,不在這裡先清掉 */
    session.value = next
    sessionStartedAt.value = loadSessionStartedAt(posMachineCode.value) ?? Date.now()
    /* 重整後 getSession 不帶 commonAmounts / mpayList,從 sessionStorage 還原 */
    commonAmounts.value = loadCommonAmounts(posMachineCode.value)
    mpayList.value = loadMpayList(posMachineCode.value)
    if (next.state === S.PAID) {
      paidAt.value = loadPaidAt(posMachineCode.value) ?? Date.now()
    }
  } catch (e) {
    if (e instanceof ApiError && e.code === '000008') {
      clearSessionId(posMachineCode.value)
      return
    }
    /* 連線錯誤就留在 Intro,讓使用者自己按開始 */
  }
}

/** 使用者按下「開始交易」才呼叫 createSession */
async function start() {
  if (starting.value) return
  if (!posMachineCode.value) {
    fatal.value = '缺少販賣機編號,請重新掃描 QR'
    return
  }
  starting.value = true
  fatal.value = ''
  /* 新 session:重置發票倒數起點,避免沿用上一筆的時間戳 */
  paidAt.value = 0
  try {
    const next = await createSession(posMachineCode.value)
    const now = Date.now()
    session.value = next
    sessionStartedAt.value = now
    saveSessionId(posMachineCode.value, next.sessionId)
    saveSessionStartedAt(posMachineCode.value, now)
    /* commonAmounts 僅此刻(createSession)回傳,存起來供之後 -3 輸入畫面用 */
    if (next.commonAmounts && next.commonAmounts.length) {
      commonAmounts.value = next.commonAmounts
      saveCommonAmounts(posMachineCode.value, next.commonAmounts)
    }
    /* mpayList 同樣僅此刻回傳,存起來供之後 WAITING_PAY 通路選單用 */
    if (next.mpayList && next.mpayList.length) {
      mpayList.value = next.mpayList
      saveMpayList(posMachineCode.value, next.mpayList)
    }
  } catch (e) {
    fatal.value = friendlyError(e)
  } finally {
    starting.value = false
  }
}

async function onCancel() {
  if (!session.value || cancelling.value) return
  cancelling.value = true
  try {
    session.value = await cancelSession(posMachineCode.value, session.value.sessionId)
  } catch (e) {
    payError.value = friendlyError(e, '取消失敗')
  } finally {
    cancelling.value = false
  }
}

/** machine-ack 後選付款方式(電子 / 現金);回傳更新後 session,payMethod 已填 */
async function onSelectPayMethod(method: QrPayMethod) {
  if (!session.value || selectingMethod.value) return
  selectingMethod.value = true
  payError.value = ''
  try {
    session.value = await selectPayMethod(
      posMachineCode.value,
      session.value.sessionId,
      method,
    )
  } catch (e) {
    payError.value = friendlyError(e, '選擇失敗,請再試一次')
  } finally {
    selectingMethod.value = false
  }
}

async function onPay(channel: PayChannel, amount?: number) {
  if (!session.value || paying.value) return
  paying.value = true
  payError.value = ''
  /* 先在 user gesture 有效時開好空白分頁佔位,等 mpay 拿到 URL 再把它導過去。
     若拖到 await 之後才呼叫 window.open,手機瀏覽器會判定成程式化開窗擋掉 */
  const newTab = window.open('about:blank', '_blank')
  if (!newTab) {
    paying.value = false
    payError.value = '瀏覽器封鎖了新分頁,請允許彈出視窗後再試一次'
    return
  }
  try {
    /* payTime 用現在;confirmUrl / cancelUrl 帶 sessionId query,
       另開分頁時新分頁的 sessionStorage 讀不到 sessionId,靠 URL 上的 sid 回填 */
    const origin = window.location.origin
    const code = encodeURIComponent(posMachineCode.value)
    const sid = encodeURIComponent(session.value.sessionId)
    const confirmUrl = `${origin}/qr/${code}/pay/confirm?sid=${sid}`
    const cancelUrl = `${origin}/qr/${code}/pay/cancel?sid=${sid}`
    const vo = await payByMobile(posMachineCode.value, session.value.sessionId, {
      payChannel: channel,
      payTime: new Date().toISOString(),
      confirmUrl,
      cancelUrl,
      /* 儲值選物(-3)才帶 amount;一般商品訂單帶 amount 會被後端拒絕 */
      ...(amount != null ? { amount } : {}),
    })
    const redirect = pickRedirectUrl(vo)
    if (redirect) {
      newTab.location.href = redirect
      awaitingExternalPay.value = true
    } else {
      /* 沒有導頁 URL → 走自動扣款,關掉空白分頁,靠 polling 看到 state 變 PAID */
      newTab.close()
    }
  } catch (e) {
    newTab.close()
    payError.value = friendlyError(e, '付款失敗')
  } finally {
    paying.value = false
  }
}

async function onSubmitInvoice(body: QrInvoiceBo) {
  if (!session.value || submittingInvoice.value) return
  submittingInvoice.value = true
  invoiceError.value = ''
  try {
    /* invoice API 直接回一份最新的 QrSessionVo(state=DONE、carrierType),不用再 poll */
    session.value = await issueInvoice(posMachineCode.value, session.value.sessionId, body)
  } catch (e) {
    invoiceError.value = friendlyError(e, '發票開立失敗')
  } finally {
    submittingInvoice.value = false
  }
}

function onClose() {
  clearSessionId(posMachineCode.value)
  session.value = null
  window.close()
  router.replace({ path: `/qr/${posMachineCode.value}` })
}

onMounted(resume)
onBeforeUnmount(stopPolling)
</script>

<style scoped>
.screen {
  min-height: 100vh;
  background: linear-gradient(180deg, #fff7ed 0%, #f9fafb 220px);
  padding-bottom: env(safe-area-inset-bottom, 16px);
}
</style>
